// Публичные API Lichess/chess.com: рейтинги + последние партии по нику.
// Сетевой слой; чистые парсеры NDJSON/архивов живут в core/coach-profile.
import {
  type CoachGame,
  type ChessComArchive,
  type Ratings,
  MAX_GAMES,
  isValidNick,
  normalizeNick,
  normalizeChessComArchiveGames,
  parseLichessNdjson,
  ratingOrNull,
} from '../core/game-sources';

/** Минимум рейтинговых партий в контроле, чтобы верить его рейтингу. */
const MIN_RATED_GAMES = 10;

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}

async function fetchJson(url: string, { signal, headers }: { signal?: AbortSignal; headers?: Record<string, string> } = {}) {
  const res = await fetch(url, { signal, headers });
  if (res.status === 404) throw new Error('nick_not_found');
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

async function fetchText(url: string, { signal, headers }: { signal?: AbortSignal; headers?: Record<string, string> } = {}) {
  const res = await fetch(url, { signal, headers });
  if (res.status === 404) throw new Error('nick_not_found');
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.text();
}

export async function fetchLichessCoachData(nick: string, { signal }: { signal?: AbortSignal } = {}) {
  const normalized = normalizeNick(nick);
  if (!isValidNick(normalized)) throw new Error('nick_not_found');
  const user = await fetchJson(`https://lichess.org/api/user/${encodeURIComponent(normalized)}`, { signal });
  // рейтинг с горсткой партий — шум (кейс tomka_tomka: 5 партий классики → «1079,
  // дно»); provisional/малые выборки не показываем ни юзеру, ни LLM
  const solid = (perf: { rating?: unknown; games?: number; prov?: boolean } | undefined) =>
    perf && !perf.prov && (perf.games || 0) >= MIN_RATED_GAMES ? ratingOrNull(perf.rating) : null;
  const ratings: Ratings = {
    blitz: solid(user.perfs?.blitz),
    rapid: solid(user.perfs?.rapid),
    classical: solid(user.perfs?.classical),
  };
  // 20 партий: у Lichess готовые [%eval] — анализ почти бесплатный по времени
  const qs = 'max=20&rated=true&evals=true&opening=true&moves=true&pgnInJson=true';
  const text = await fetchText(`https://lichess.org/api/games/user/${encodeURIComponent(normalized)}?${qs}`, {
    signal,
    headers: { accept: 'application/x-ndjson' },
  });
  return { nick: normalized, ratings, games: parseLichessNdjson(text) };
}

export async function fetchChessComCoachData(nick: string, { signal }: { signal?: AbortSignal } = {}) {
  const normalized = normalizeNick(nick);
  if (!isValidNick(normalized)) throw new Error('nick_not_found');
  const stats = await fetchJson(`https://api.chess.com/pub/player/${encodeURIComponent(normalized)}/stats`, { signal });
  // как и у Lichess: рейтинг без выборки (см. MIN_RATED_GAMES) не учитываем
  const solid = (cat: { last?: { rating?: unknown }; record?: { win?: number; loss?: number; draw?: number } } | undefined) => {
    const r = cat?.record;
    const games = (r?.win || 0) + (r?.loss || 0) + (r?.draw || 0);
    return games >= MIN_RATED_GAMES ? ratingOrNull(cat?.last?.rating) : null;
  };
  const ratings: Ratings = {
    blitz: solid(stats.chess_blitz),
    rapid: solid(stats.chess_rapid),
    classical: solid(stats.chess_daily),
  };
  const archiveList = await fetchJson(`https://api.chess.com/pub/player/${encodeURIComponent(normalized)}/games/archives`, { signal });
  const urls: string[] = (archiveList.archives || []).slice(-3).reverse();
  const archives: ChessComArchive[] = [];
  for (const url of urls) {
    assertNotAborted(signal);
    archives.push(await fetchJson(url, { signal }));
    if (normalizeChessComArchiveGames(archives).length >= MAX_GAMES) break;
  }
  return { nick: normalized, ratings, games: normalizeChessComArchiveGames(archives) };
}

export async function collectCoachGames(
  { lichess, chesscom }: { lichess?: string; chesscom?: string } = {},
  opts: { signal?: AbortSignal } = {},
) {
  const result = {
    nicks: { lichess: normalizeNick(lichess), chesscom: normalizeNick(chesscom) },
    ratings: { lichess: {} as Partial<Ratings>, chesscom: {} as Partial<Ratings> },
    games: [] as CoachGame[],
  };
  if (result.nicks.lichess) {
    const data = await fetchLichessCoachData(result.nicks.lichess, opts);
    result.ratings.lichess = data.ratings;
    result.games.push(...data.games);
  }
  if (result.nicks.chesscom) {
    const data = await fetchChessComCoachData(result.nicks.chesscom, opts);
    result.ratings.chesscom = data.ratings;
    result.games.push(...data.games);
  }
  return result;
}

/** История рейтинга Lichess (официальный бесплатный API). [{t: unix-сек, r}] по контролю. */
export async function fetchLichessRatingHistory(
  nick: string,
  speed: 'blitz' | 'rapid' | 'classical',
): Promise<{ t: number; r: number }[]> {
  const data = await fetchJson(`https://lichess.org/api/user/${encodeURIComponent(normalizeNick(nick))}/rating-history`);
  const wanted = speed === 'blitz' ? 'Blitz' : speed === 'rapid' ? 'Rapid' : 'Classical';
  const perf = (data as { name: string; points: [number, number, number, number][] }[]).find((x) => x.name === wanted);
  return (perf?.points || []).map(([y, m, d, r]) => ({ t: Math.round(Date.UTC(y, m, d) / 1000), r }));
}

/** Аватар chess.com (у Lichess аватаров нет). */
export async function fetchChessComAvatar(nick: string): Promise<string | null> {
  try {
    const p = await fetchJson(`https://api.chess.com/pub/player/${encodeURIComponent(normalizeNick(nick))}`);
    return typeof p.avatar === 'string' ? p.avatar : null;
  } catch {
    return null;
  }
}
